/** 将代理测量 JSON 解析为表格行（兼容 56yorky 中英文字段与哲盟 Q5） */

export interface AgentMeasurementRow {
  key: string;
  cartonId: string;
  dimensions: string;
  volume: string;
  volumeWeight: string;
  grossWeight: string;
  statusText: string;
  restrictText: string;
  deliveryReference: string;
}

const MEASURE_STATUS_LABEL: Record<number, string> = {
  0: '新增',
  1: '已下单',
  2: '收货中',
  3: '已收货',
  4: '待确认',
  5: '已确认',
  6: '已删除',
  7: '调拨中',
  8: '扣货',
  9: '退货',
};

function asMap(raw: unknown): Record<string, unknown> | null {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>;
  }
  return null;
}

function firstValue(map: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (!(k in map) || map[k] == null) continue;
    const v = map[k];
    if (typeof v === 'string' && (v.trim() === '' || v.trim() === '-')) continue;
    return v;
  }
  return null;
}

function firstString(map: Record<string, unknown>, ...keys: string[]): string | null {
  const v = firstValue(map, ...keys);
  if (v == null) return null;
  const s = String(v).trim();
  return s && s !== '-' ? s : null;
}

function formatNum(v: unknown): string {
  if (v == null) return '-';
  const s = String(v).trim();
  if (!s || s === '-') return '-';
  const n = Number(s);
  if (!Number.isNaN(n)) {
    return String(n);
  }
  return s;
}

function parseSlashDims(dim: string | null): string | null {
  if (!dim) return null;
  const parts = dim.trim().split(/[/xX×]/);
  if (parts.length < 3) return null;
  return `${formatNum(parts[0])}×${formatNum(parts[1])}×${formatNum(parts[2])}`;
}

function formatDimensions(map: Record<string, unknown>): string {
  const slash =
    parseSlashDims(firstString(map, '仓库长/宽/高', '长/宽/高', 'warehouseSize', 'size'));
  if (slash) return slash;
  const l = firstValue(map, 'boxLong', 'length', 'lengthCm', '仓库长');
  const w = firstValue(map, 'boxWide', 'width', 'widthCm', '仓库宽');
  const h = firstValue(map, 'boxHigh', 'height', 'heightCm', '仓库高');
  if (l != null && w != null && h != null) {
    return `${formatNum(l)}×${formatNum(w)}×${formatNum(h)}`;
  }
  return '-';
}

function formatStatus(map: Record<string, unknown>): string {
  const raw = firstValue(map, 'status', '状态');
  if (raw == null) return '-';
  if (typeof raw === 'string' && /[\u4e00-\u9fff]/.test(raw)) return raw;
  const n = Number(raw);
  if (!Number.isNaN(n) && MEASURE_STATUS_LABEL[n] != null) {
    return MEASURE_STATUS_LABEL[n];
  }
  return String(raw);
}

function formatRestrict(map: Record<string, unknown>): string {
  const flag = firstString(map, 'isAstrict', 'astrict');
  const remark = firstString(map, 'astrictRemark', '备注');
  let label = '-';
  if (flag != null) {
    const upper = flag.toUpperCase();
    if (upper === 'T' || upper === 'TRUE' || upper === '1' || flag === '是') label = '是';
    else if (upper === 'F' || upper === 'FALSE' || upper === '0' || flag === '否') label = '否';
    else label = flag;
  }
  if (remark) {
    return label === '-' ? remark : `${label}（${remark}）`;
  }
  return label;
}

function rowFromMap(map: Record<string, unknown>, index: number): AgentMeasurementRow {
  const cartonId =
    firstString(map, 'boxNo', '箱号', 'childJobno', 'cartonId', 'cartonNo') || '-';
  return {
    key: String(index),
    cartonId,
    dimensions: formatDimensions(map),
    volume: formatNum(firstValue(map, 'volume', '仓库体积', '体积', 'volumeCbm')),
    volumeWeight: formatNum(firstValue(map, 'volumeWeight', '仓库体积重', '体积重', 'billingWeight')),
    grossWeight: formatNum(firstValue(map, 'grossWeight', '仓库毛重', '毛重', 'actual', 'weight')),
    statusText: formatStatus(map),
    restrictText: formatRestrict(map),
    deliveryReference: firstString(map, 'deliveryReference', '跟踪号') || '-',
  };
}

export type ParseAgentMeasurementResult =
  | { ok: true; rows: AgentMeasurementRow[]; rawText: string }
  | { ok: false; rows: []; rawText: string; error: string };

/**
 * 解析 agentMeasurementDataJson。
 * 支持：数组、{ data: [] }、{ measureList: [] }、单个对象。
 */
export function parseAgentMeasurementJson(json: string | null | undefined): ParseAgentMeasurementResult {
  if (!json || !json.trim()) {
    return { ok: true, rows: [], rawText: '' };
  }
  const rawText = json.trim();
  try {
    const parsed = JSON.parse(rawText);
    let list: unknown[] = [];
    if (Array.isArray(parsed)) {
      list = parsed;
    } else if (parsed && typeof parsed === 'object') {
      const obj = parsed as Record<string, unknown>;
      if (Array.isArray(obj.measureList)) list = obj.measureList;
      else if (Array.isArray(obj.data)) list = obj.data;
      else list = [parsed];
    }
    const rows: AgentMeasurementRow[] = [];
    list.forEach((item, i) => {
      const map = asMap(item);
      if (map) rows.push(rowFromMap(map, i));
    });
    return { ok: true, rows, rawText: JSON.stringify(parsed, null, 2) };
  } catch {
    return { ok: false, rows: [], rawText, error: '数据格式无法解析' };
  }
}
